import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSelector } from 'react-redux';
import {
  Box,
  VStack,
  Text,
  Icon,
  Flex,
  useColorModeValue,
  Divider,
  useDisclosure,
  Avatar,
  Badge,
  Collapse,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiHome,
  FiBarChart2,
  FiSettings,
  FiBook,
  FiUsers,
  FiUserCheck,
  FiKey,
  FiCreditCard,
  FiMessageSquare,
  FiMic,
  FiVolume2,
  FiBell,
  FiTool,
  FiFileText,
  FiPhone,
  FiList,
  FiClock,
  FiChevronDown,
  FiChevronUp,
  FiGlobe,
  FiPieChart,
  FiPlus,
  FiChevronLeft,
  FiChevronRight,
  FiMenu,
  FiCalendar,
  FiDollarSign,
  FiUserPlus,
} from 'react-icons/fi';
import { Building2 } from 'lucide-react';
import NotificationPopover from './NotificationPopover';

const NavItem = ({ icon, children, to, isActive, pl = 3 }) => {
  const activeBg = useColorModeValue('purple.100', 'purple.900');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const activeColor = useColorModeValue('purple.600', 'purple.200');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const hoverTextColor = useColorModeValue('gray.800', 'gray.100');
  const iconColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box
      as={Link}
      to={to}
      style={{ width: '100%', textDecoration: 'none' }}
      _hover={{ textDecoration: 'none' }}
    >
      <Flex
        align="center"
        p="3"
        mx="2"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        bg={isActive ? activeBg : 'transparent'}
        color={isActive ? activeColor : textColor}
        _hover={{
          bg: isActive ? activeBg : hoverBg,
          color: activeColor,
          transform: 'translateX(4px)',
        }}
        transition="all 0.2s ease-in-out"
        pl={pl}
        w="full"
        justifyContent="flex-start"
      >
        <Icon
          mr="3"
          fontSize="18"
          as={icon}
          transition="margin-right 0.2s ease-in-out, transform 0.2s ease-in-out"
          transform={isActive ? 'scale(1.1)' : 'scale(1)'}
          color={isActive ? activeColor : iconColor}
          _groupHover={{
            color: activeColor,
          }}
          minW="18px"
        />
        <Text
          fontWeight={isActive ? 'bold' : 'normal'}
          fontSize="sm"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          flexShrink={0}
          color={isActive ? activeColor : textColor}
          _groupHover={{
            color: hoverTextColor,
          }}
        >
          {children}
        </Text>
      </Flex>
    </Box>
  );
};

const CollapsibleNavItem = ({ icon, label, children, defaultOpen = false, to }) => {
  const { isOpen: isItemOpen, onToggle } = useDisclosure({ defaultIsOpen: defaultOpen });
  const location = useLocation();
  const navigate = useNavigate();
  const activeColor = useColorModeValue('purple.600', 'purple.200');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const activeBg = useColorModeValue('purple.100', 'purple.900');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const hoverTextColor = useColorModeValue('gray.800', 'gray.100');

  const isChildActive = React.Children.toArray(children).some(child => {
    if (child.props && child.props.to) {
      const childPath = child.props.to.split('?')[0].split('#')[0];
      const currentPath = location.pathname.split('?')[0].split('#')[0];
      return currentPath.toLowerCase().startsWith(childPath.toLowerCase());
    }
    return false;
  });

  const handleClick = (e) => {
    e.preventDefault();
    onToggle();
    if (to && !isChildActive) {
      navigate(to);
    }
  };

  return (
    <Box>
      <Tooltip label={label} placement="right" isDisabled={false}>
        <Flex
          align="center"
          p="3"
          mx="2"
          borderRadius="lg"
          role="group"
          cursor="pointer"
          onClick={handleClick}
          bg={isChildActive ? activeBg : 'transparent'}
          color={isChildActive ? activeColor : textColor}
          _hover={{
            bg: isChildActive ? activeBg : hoverBg,
            color: activeColor,
            transform: 'translateX(4px)',
          }}
          transition="all 0.2s ease-in-out"
          w="full"
          justifyContent="flex-start"
        >
          <Icon
            mr="3"
            fontSize="18"
            as={icon}
            transition="margin-right 0.2s ease-in-out, transform 0.2s ease-in-out"
            transform={isChildActive ? 'scale(1.1)' : 'scale(1)'}
            color={isChildActive ? activeColor : useColorModeValue('gray.500', 'gray.400')}
            _groupHover={{
              color: activeColor,
            }}
            minW="18px"
          />
          <Text
            fontWeight={isChildActive ? 'bold' : 'normal'}
            fontSize="sm"
            flex="1"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            flexShrink={0}
            color={isChildActive ? activeColor : textColor}
            _groupHover={{
              color: hoverTextColor,
            }}
          >
            {label}
          </Text>
          <Icon
            as={isItemOpen ? FiChevronUp : FiChevronDown}
            transition="opacity 0.2s ease-in-out"
            color={isChildActive ? activeColor : useColorModeValue('gray.500', 'gray.400')}
          />
        </Flex>
      </Tooltip>
      <Collapse in={isItemOpen} animateOpacity>
        <VStack align="stretch" pl={8} spacing={1}>
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, { pl: 8 });
            }
            return child;
          })}
        </VStack>
      </Collapse>
    </Box>
  );
};

const SidebarContent = () => {
  const location = useLocation();
  const { user: currentUser } = useSelector((state) => state.auth);
  const isActive = (path) => location.pathname === path;
  const isChildActive = (parentPath) => location.pathname.startsWith(parentPath) && location.pathname !== parentPath;

  // Calculate role permissions
  const rolePermissions = {
    isSuperAdmin: currentUser?.role === 'superadmin',
    isAdmin: currentUser?.role === 'admin',
    isUser: currentUser?.role === 'user'
  };

  return (
    <Box
      w="full"
      h="full"
      py={5}
      overflowY="auto"
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'gray.200',
          borderRadius: '24px',
        },
      }}
    >
      {currentUser && (
        <Flex px={4} py={5} align="center">
          <Avatar size="sm" name={currentUser.name} src={currentUser.avatar} />
          <Box ml={4}>
            <Text fontWeight="bold" fontSize="sm">
              {currentUser.name}
            </Text>
            <Badge fontSize="xs" colorScheme="blue">
              {currentUser.role === 'superadmin' ? 'SUPERADMIN' : currentUser.role}
            </Badge>
          </Box>
        </Flex>
      )}

      <Divider />

      <VStack spacing={1} align="stretch">
        {/* Superadmin: Show all links */}
        {rolePermissions.isSuperAdmin && (
          <>
            <NavItem icon={FiHome} to="/admin/dashboard" isActive={isActive('/admin/dashboard')}>
              Super Admin Dashboard
            </NavItem>
            <NavItem icon={FiBarChart2} to="/admin/admin-dashboard" isActive={isActive('/admin/admin-dashboard')}>
              Admin Dashboard
            </NavItem>
            <NavItem icon={FiUserCheck} to="/admin/user-management" isActive={isActive('/admin/user-management')}>
              user Account Management
            </NavItem>
            <NavItem icon={FiTool} to="/admin/widget-management" isActive={isActive('/admin/widget-management')}>
              Widget Management
            </NavItem>
            <CollapsibleNavItem icon={FiMessageSquare} label="Chatbot Agent" defaultOpen={isChildActive('/admin/chatbot-agent')} to="/admin/chatbot-agent/analytics">
              <NavItem icon={FiPieChart} to="/admin/chatbot-agent/analytics" isActive={isActive('/admin/chatbot-agent/analytics')} pl={8}>Analytics</NavItem>
              <NavItem icon={FiBook} to="/admin/chatbot-agent/knowledge-base" isActive={isActive('/admin/chatbot-agent/knowledge-base')} pl={8}>Knowledge Base</NavItem>
              <NavItem icon={FiMic} to="/admin/chatbot-agent/transcript" isActive={isActive('/admin/chatbot-agent/transcript')} pl={8}>Transcript</NavItem>
            </CollapsibleNavItem>
            <CollapsibleNavItem icon={FiMic} label="Voice Agent" defaultOpen={isChildActive('/admin/voice-agent')} to="/admin/voice-agent/knowledge-base">
              <NavItem icon={FiBook} to="/admin/voice-agent/knowledge-base" isActive={isActive('/admin/voice-agent/knowledge-base')} pl={8}>Knowledge Base</NavItem>
              <NavItem icon={FiPhone} to="/admin/voice-agent/phone-numbers" isActive={isActive('/admin/voice-agent/phone-numbers')} pl={8}>Phone Numbers</NavItem>
              <NavItem icon={FiList} to="/admin/voice-agent/batch-call" isActive={isActive('/admin/voice-agent/batch-call')} pl={8}>Batch Call</NavItem>
              <NavItem icon={FiClock} to="/admin/voice-agent/call-history" isActive={isActive('/admin/voice-agent/call-history')} pl={8}>Call History</NavItem>
              <NavItem icon={FiPieChart} to="/admin/voice-agent/analytics" isActive={isActive('/admin/voice-agent/analytics')} pl={8}>Analytics</NavItem>
            </CollapsibleNavItem>
            <NavItem icon={Building2} to="/admin/club-management" isActive={isActive('/admin/club-management')}>
              user Management
            </NavItem>
            <NavItem icon={FiCreditCard} to="/admin/subscription-plans" isActive={isActive('/admin/subscription-plans')}>
              Subscription Plans
            </NavItem>
            <NavItem icon={FiFileText} to="/admin/documents" isActive={isActive('/admin/documents')}>
              Documents Management
            </NavItem>
            <NavItem icon={FiKey} to="/admin/api-keys" isActive={isActive('/admin/api-keys')}>
              API Keys
            </NavItem>
            <NavItem icon={FiSettings} to="/admin/settings" isActive={isActive('/admin/settings')}>
              General Settings
            </NavItem>
            <NavItem icon={FiBell} to="/admin/notifications" isActive={isActive('/admin/notifications')}>
              All Notifications
            </NavItem>
            <NavItem icon={FiList} to="/admin/leads-management" isActive={isActive('/admin/leads-management')}>
              Leads Management
            </NavItem>
          </>
        )}

        {/* Admin-only Links (if not superadmin) */}
        {!rolePermissions.isSuperAdmin && rolePermissions.isAdmin && (
          <>
            <NavItem icon={FiHome} to="/admin/admin-dashboard" isActive={isActive('/admin/admin-dashboard')}>
              Admin Dashboard
            </NavItem>
            <NavItem icon={FiUserCheck} to="/admin/user-management" isActive={isActive('/admin/user-management')}>
              user Account Management
            </NavItem>
            <NavItem icon={FiTool} to="/admin/widget-management" isActive={isActive('/admin/widget-management')}>
              Widget Management
            </NavItem>
            <CollapsibleNavItem icon={FiMessageSquare} label="Chatbot Agent" defaultOpen={isChildActive('/admin/chatbot-agent')} to="/admin/chatbot-agent/analytics">
              <NavItem icon={FiPieChart} to="/admin/chatbot-agent/analytics" isActive={isActive('/admin/chatbot-agent/analytics')} pl={8}>Analytics</NavItem>
              <NavItem icon={FiBook} to="/admin/chatbot-agent/knowledge-base" isActive={isActive('/admin/chatbot-agent/knowledge-base')} pl={8}>Knowledge Base</NavItem>
              <NavItem icon={FiMic} to="/admin/chatbot-agent/transcript" isActive={isActive('/admin/chatbot-agent/transcript')} pl={8}>Transcript</NavItem>
            </CollapsibleNavItem>
            <CollapsibleNavItem icon={FiMic} label="Voice Agent" defaultOpen={isChildActive('/admin/voice-agent')} to="/admin/voice-agent/knowledge-base">
              <NavItem icon={FiBook} to="/admin/voice-agent/knowledge-base" isActive={isActive('/admin/voice-agent/knowledge-base')} pl={8}>Knowledge Base</NavItem>
              <NavItem icon={FiPhone} to="/admin/voice-agent/phone-numbers" isActive={isActive('/admin/voice-agent/phone-numbers')} pl={8}>Phone Numbers</NavItem>
              <NavItem icon={FiList} to="/admin/voice-agent/batch-call" isActive={isActive('/admin/voice-agent/batch-call')} pl={8}>Batch Call</NavItem>
              <NavItem icon={FiClock} to="/admin/voice-agent/call-history" isActive={isActive('/admin/voice-agent/call-history')} pl={8}>Call History</NavItem>
              <NavItem icon={FiPieChart} to="/admin/voice-agent/analytics" isActive={isActive('/admin/voice-agent/analytics')} pl={8}>Analytics</NavItem>
            </CollapsibleNavItem>
            <NavItem icon={Building2} to="/admin/club-management" isActive={isActive('/admin/club-management')}>
              user Management
            </NavItem>
            <NavItem icon={FiCreditCard} to="/admin/subscription-plans" isActive={isActive('/admin/subscription-plans')}>
              Subscription Plans
            </NavItem>
            <NavItem icon={FiFileText} to="/admin/documents" isActive={isActive('/admin/documents')}>
              Documents Management
            </NavItem>
            <NavItem icon={FiBell} to="/admin/notifications" isActive={isActive('/admin/notifications')}>
              All Notifications
            </NavItem>
            <NavItem icon={FiList} to="/admin/leads-management" isActive={isActive('/admin/leads-management')}>
              Leads Management
            </NavItem>
          </>
        )}

        {/* User-specific Links */}
        {rolePermissions.isUser && (
          <Box w="full" px={2}>
            <VStack spacing={1}>
              <NavItem icon={FiTool} to="/widgets" isActive={isActive('/widgets')}>
                My Widgets
              </NavItem>
              <NavItem icon={FiBell} to="/user/notifications" isActive={isActive('/user/notifications')}>
                My Notifications
              </NavItem>
              <NavItem icon={FiSettings} to="/user/settings" isActive={isActive('/user/settings')}>
                My Settings
              </NavItem>
              <NavItem icon={FiCreditCard} to="/user/billing" isActive={isActive('/user/billing')}>
                My Billing
              </NavItem>
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

const Sidebar = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box
      as="nav"
      pos="fixed"
      left={0}
      w={{ base: 'full', md: '64' }}
      h="full"
      bg={bgColor}
      borderRight="1px"
      borderColor={borderColor}   
      zIndex={20}   
    >
      <Box
        w="full"
        h="full"
        overflowY="auto"
        overflowX="hidden"
        pb="4"
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: useColorModeValue('gray.300', 'gray.600'),
            borderRadius: '24px',
          },
        }}
      >
        <SidebarContent />
      </Box>
    </Box>
  );
};

export default Sidebar;